import { Box, Button, Collapse, TableFooter, TableRow, TableCell, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import useGrandTotal from "../../hooks/useGrandTotal";
import {
  NumberField,
  RecordContextProvider,
  TextField,
  usePermissions,
} from "react-admin";

const toNumber = (value) => {
  const number = Number(value);
  if (Number.isFinite(number)) {
    return number;
  }
  return 0;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value));

const formatPercent = (value) =>
  `${new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value) * 100)}%`;

const SalesmanIncentiveFooterList = () => {
  const { data, isLoading, error } = useGrandTotal();
  const { permissions } = usePermissions();
  const [showProfitDetail, setShowProfitDetail] = useState(false);

  const colSpan = permissions && permissions.update_company ? 18 : 17;

  const profitNumbers = useMemo(() => {
    const profitNetAfterTax = toNumber(data?.profit_net_after_tax);
    const profitBaseSaleNet = toNumber(data?.profit_base_sale_net);
    const profitTaxIn = toNumber(data?.profit_tax_in);
    const profitTaxOut = toNumber(data?.profit_tax_out);
    const profitDenominator = toNumber(data?.profit_denominator);
    const profitRatioFromBreakdown =
      profitDenominator !== 0 ? profitNetAfterTax / profitDenominator : 0;

    return {
      profitNetAfterTax,
      profitBaseSaleNet,
      profitTaxIn,
      profitTaxOut,
      profitDenominator,
      profitRatioFromBreakdown,
    };
  }, [data]);

  // if (error) return <Error />;

  return (
    <RecordContextProvider value={data ? data : {}}>
      <TableFooter>
        <TableRow
          sx={{
            "& td": {
              borderTop: "1px solid rgba(224, 224, 224, 1) !important",
            },
            "& .MuiTypography-root": {
              color: (theme) => theme.palette.primary.main,
              fontWeight: "bold",
            },
          }}
        >
          {permissions && permissions.update_company ? (
            <TableCell className="si-sticky-company" />
          ) : null}
          <TableCell className="si-sticky-name">
            <RecordContextProvider value={{ name: "GRAND TOTAL" }}>
              <TextField source="name" />
            </RecordContextProvider>
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_sale_net_g" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_sale_net_phm" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_sale_net_z" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_sale_net" textAlign="right" />
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_gross_incentive_g" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_gross_incentive_phm" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_gross_incentive_z" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField
              source="total_gross_incentive_total"
              textAlign="right"
            />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_penalty" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_net_incentive" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField
              source="profit_ratio"
              textAlign="right"
              options={{
                style: "percent",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }}
            />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="profit_multiplier" textAlign="right" />
          </TableCell>
          <TableCell sx={{ textAlign: "right" }}>
            <NumberField source="total_final_incentive" textAlign="right" />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={colSpan}
            sx={{
              borderTop: "none",
              backgroundColor: "rgba(103, 58, 183, 0.04)",
              py: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rincian Perhitungan Rasio Profit
              </Typography>
              <Button
                size="small"
                onClick={() => setShowProfitDetail((prev) => !prev)}
              >
                {showProfitDetail ? "Sembunyikan" : "Tampilkan"}
              </Button>
            </Box>
            <Collapse in={showProfitDetail} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 0.5, display: "grid", gap: 0.25 }}>
                <Typography variant="caption">
                  Rumus dasar: Rasio Profit = Laba/Rugi Bersih Setelah Pajak / (Penjualan Bersih + Tax Out - (Tax Out - Tax In))
                </Typography>
                <Typography variant="caption">
                  Laba/Rugi Bersih Setelah Pajak: {formatCurrency(profitNumbers.profitNetAfterTax)}
                </Typography>
                <Typography variant="caption">
                  Penjualan Bersih (basis Income Statement): {formatCurrency(profitNumbers.profitBaseSaleNet)}
                </Typography>
                <Typography variant="caption">
                  Tax Out: {formatCurrency(profitNumbers.profitTaxOut)}
                </Typography>
                <Typography variant="caption">
                  Tax In: {formatCurrency(profitNumbers.profitTaxIn)}
                </Typography>
                <Typography variant="caption">
                  Dasar Pembagi (hasil): {formatCurrency(profitNumbers.profitDenominator)}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Hasil Rasio Profit: {formatPercent(profitNumbers.profitRatioFromBreakdown)}
                </Typography>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </TableFooter>
    </RecordContextProvider>
  );
};

export default SalesmanIncentiveFooterList;
